import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Coding, DocumentReference, ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import {
  Box,
  Checkbox,
  Drawer,
  FileInput,
  Flex,
  InputLabel,
  Progress,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";

import { IconFileUpload } from "@tabler/icons-react";
import isEqual from "lodash/isEqual";

import { DisabledReasonButton, NO_CHANGES_REASON } from "@/components/DisabledReason";
import { TextWrapper } from "@/components/TextWrapper";
import ValueSetSelect from "@/components/ValueSetSelect";
import { ProfileContext } from "@/contexts/profile";
import { QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import { useAppDispatch } from "@/store";
import { upsertUploadedFile } from "@/store/slices/uploaded-files";
import { ACCEPTED_FILE_TYPES, AllowedFileTypes } from "@/types/uploads";
import useZenosDichotomyParadox from "@/utils/hooks/use-zenos-dichotomy-paradox";
import { toast } from "@/utils/notifications";

import { upsertDocumentReference } from "./services/file-uploads";
import { FileUploadAction } from "./types/file-uploads";
import { getInitialValuesFromDocumentReference } from "./utils/file-uploads";
import {
  buildFileUploadFormSchema,
  FILE_UPLOAD_INITIAL_VALUES,
  FileUploadFormType,
} from "./zod-schemas/file-uploads";

/**
 * Mantine v8's Path<T> recursively enumerates all dot-notation paths through a type.
 * FHIR types like Coding contain `extension?: Extension[]` and Extension contains
 * `valueCoding?: Coding`, creating infinite mutual recursion during type checking.
 * Pick<Coding, ...> with only scalar string fields breaks the cycle while keeping
 * Coding values directly assignable (Coding extends the pick structurally).
 */
type FileUploadFormValues = {
  fileName: string;
  file: File | null;
  fileType: Pick<Coding, "system" | "code" | "display"> | null;
  description?: string;
  visibleToClient: boolean;
  fileChanged: boolean;
};

interface FileUploadDrawerProps {
  opened: boolean;
  onClose: () => void;
  onPostUpload?: (action: FileUploadAction) => void;
  documentReference?: DocumentReference;
  defaultFileType?: Coding | null;
  encounterId?: string;
  relatedInvoiceId?: string;
  documentCategory?: Coding;
  practiceOnly?: boolean;
  acceptedFileTypes?: AllowedFileTypes[];
  fileTypeErrorMessage?: string;
}

export default function FileUploadDrawer({
  opened,
  onClose,
  onPostUpload,
  documentReference,
  defaultFileType,
  encounterId,
  relatedInvoiceId,
  documentCategory,
  practiceOnly,
  acceptedFileTypes,
  fileTypeErrorMessage,
}: FileUploadDrawerProps) {
  const { clientId } = useParams<{ clientId: string }>();
  const { isPatient, profile } = useContext(ProfileContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const abortController = useRef<AbortController | null>(null);
  const [uploadProgress, startUploadProgress, stopUploadProgress, setUploadProgress] =
    useZenosDichotomyParadox();
  const [initialValues, setInitialValues] = useState<FileUploadFormValues>(
    FILE_UPLOAD_INITIAL_VALUES,
  );

  // A practice-only file is never client-visible, so the checkbox has nothing to choose.
  const hideClientVisibility = Boolean(practiceOnly);
  const isPatientUploadingOwnFile = isPatient && profile?.id === clientId;

  const fileUploadFormSchema = useMemo(
    () => buildFileUploadFormSchema(acceptedFileTypes, fileTypeErrorMessage),
    [acceptedFileTypes, fileTypeErrorMessage],
  );

  const form = useForm<FileUploadFormValues>({
    initialValues: {
      ...FILE_UPLOAD_INITIAL_VALUES,
      visibleToClient:
        !hideClientVisibility && isPatientUploadingOwnFile
          ? true
          : FILE_UPLOAD_INITIAL_VALUES.visibleToClient,
    },
    validate: zodResolver(fileUploadFormSchema),
  });

  const { values: formValues, setValues: formSetValues } = form;

  useEffect(() => {
    // Re-runs on `opened` so a persistently mounted drawer (e.g. Statement Detail's) reapplies
    // `defaultFileType` on reopen — `form.reset()` on close clears it back to the form's static
    // initial values, which carry no default file type.
    if (!opened) return;

    if (documentReference) {
      const initialValues = getInitialValuesFromDocumentReference(documentReference);
      setInitialValues(initialValues);
      formSetValues(initialValues);
    } else {
      if (defaultFileType) {
        form.setFieldValue("fileType", defaultFileType);
      }
      if (!hideClientVisibility && isPatientUploadingOwnFile) {
        form.setFieldValue("visibleToClient", true);
      }
    }
    // form omitted: including it causes an infinite loop (formSetValues updates form state → new
    // form reference → effect re-runs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, documentReference, defaultFileType, formSetValues, isPatientUploadingOwnFile]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (abortController.current) {
        abortController.current.abort();
        abortController.current = null;
      }
    };
    document.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      handleBeforeUnload();
      document.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const hasChanges = useMemo(() => {
    return !isEqual(formValues, initialValues);
  }, [formValues, initialValues]);

  const handleSubmit = async (values: FileUploadFormValues) => {
    startUploadProgress();
    setIsSubmitting(true);
    abortController.current = new AbortController();

    try {
      const upsertedDocumentReference = await upsertDocumentReference({
        medplum,
        clientId: clientId!,
        values: {
          ...values,
          visibleToClient: hideClientVisibility ? false : values.visibleToClient,
        } as unknown as FileUploadFormType,
        signal: abortController.current.signal,
        documentReference,
        encounterId,
        relatedInvoiceId,
        category: documentCategory,
        practiceOnly,
      });

      if (!upsertedDocumentReference) {
        handleUploadFailure();
        return;
      }

      dispatch(upsertUploadedFile(upsertedDocumentReference));
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        handleUploadCanceled();
        return;
      }

      handleUploadFailure();
      throw error;
    } finally {
      setUploadProgress(100);
      abortController.current = null;
    }

    // Delay lets the user see the progress bar reach completion before the drawer closes
    setTimeout(
      () => handlePostUpload(documentReference ? FileUploadAction.Update : FileUploadAction.Create),
      600,
    );
  };

  const handleUploadCanceled = () => {
    if (abortController.current) {
      abortController.current.abort();
    }
    setIsSubmitting(false);
    stopUploadProgress();
  };

  const handleUploadFailure = () => {
    form.setFieldError("file", "Upload failed! Please retry.");
    setIsSubmitting(false);
    stopUploadProgress();
  };

  const handlePostUpload = (action: FileUploadAction) => {
    setIsSubmitting(false);
    onPostUpload?.(action);

    if (!formValues.fileChanged) {
      toast({
        title: "File updated",
        message: "File updated successfully",
        type: "success",
      });
    } else {
      stopUploadProgress();
      toast({
        title: "File uploaded",
        message: "File uploaded successfully",
        type: "success",
      });
    }

    handleClose();
  };

  const handleClose = () => {
    setInitialValues(FILE_UPLOAD_INITIAL_VALUES);
    form.reset();
    onClose();
  };

  const handleFileChange = (file: File | null) => {
    form.getInputProps("file").onChange(file);
    form.setFieldValue("fileChanged", true);
  };

  return (
    <Drawer.Root
      trapFocus
      size={512}
      opened={opened}
      position="right"
      onClose={handleClose}
      closeOnEscape={false}
      closeOnClickOutside={false}
    >
      <Drawer.Overlay />
      <Drawer.Content>
        <form noValidate onSubmit={form.onSubmit((values) => handleSubmit(values))}>
          <Drawer.Header h={64} px="md" bg="gray.0">
            <Flex w="100%" gap="md" align="center" justify="space-between">
              <Flex gap="md" align="center" style={{ flex: 1 }}>
                <Drawer.CloseButton aria-label="Close" />
                <Drawer.Title style={{ flex: 1 }}>
                  <TextWrapper fz="lg" variant="bold" style={{ wordBreak: "break-word" }}>
                    Upload File
                  </TextWrapper>
                </Drawer.Title>
              </Flex>
              <DisabledReasonButton
                type="submit"
                miw="fit-content"
                loading={isSubmitting}
                disabledReason={hasChanges ? undefined : NO_CHANGES_REASON}
              >
                Save
              </DisabledReasonButton>
            </Flex>
          </Drawer.Header>
          <Drawer.Body p="lg">
            <Flex gap="lg" direction="column">
              <TextInput
                required
                label="File Name"
                {...form.getInputProps("fileName")}
                disabled={isSubmitting}
              />
              {isSubmitting && formValues.fileChanged ? (
                <Box>
                  <InputLabel>Uploading File</InputLabel>
                  <Progress.Root size={24}>
                    <Progress.Section color="green.9" value={uploadProgress}>
                      <Progress.Label>
                        <TextWrapper fz="sm" variant="bold">
                          Uploading
                        </TextWrapper>
                      </Progress.Label>
                    </Progress.Section>
                  </Progress.Root>
                </Box>
              ) : (
                <FileInput
                  size="md"
                  label="Upload File"
                  leftSection={<IconFileUpload size={16} />}
                  {...form.getInputProps("file")}
                  required
                  clearable
                  disabled={isSubmitting}
                  onChange={handleFileChange}
                  accept={acceptedFileTypes?.join(",") ?? ACCEPTED_FILE_TYPES}
                />
              )}
              <ValueSetSelect
                required
                label="File Type"
                placeholder="Select"
                disabled={isSubmitting}
                error={form.errors.fileType}
                answerValueSet={QuiltedHealthValueSets.UploadedDocumentTypes}
                value={form.values.fileType as unknown as ValueSetExpansionContains | null}
                onChange={(val) =>
                  form.setFieldValue(
                    "fileType",
                    val as Pick<Coding, "system" | "code" | "display"> | null,
                  )
                }
              />
              <Textarea
                autosize
                minRows={3}
                maxRows={12}
                label="Description"
                {...form.getInputProps("description")}
                disabled={isSubmitting}
              />
              {!hideClientVisibility && !isPatientUploadingOwnFile && (
                <Checkbox
                  label="Visible to client"
                  {...form.getInputProps("visibleToClient", { type: "checkbox" })}
                  disabled={isSubmitting}
                />
              )}
            </Flex>
          </Drawer.Body>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
}
